import { useId, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError } from "@/lib/api";
import type { AccountDocument } from "@/lib/brokerAccounts";
import { documentRejection } from "@/lib/brokerBonds";
import {
  acceptAttribute,
  documentRuleMessage,
  screenDocument,
  type DocumentRules,
} from "@/lib/brokerDocumentTypes";
import { messageFor } from "@/lib/errors";
import {
  deleteAccountDocument,
  uploadAccountDocument,
  type RequirementDocumentType,
} from "@/lib/signalAdvanceEvidence";
import { FileDropTarget } from "@/components/FileDropTarget";
import { SelectField } from "@/components/SelectField";

// BRK-S330 — one upload slot for documents filed on the ACCOUNT: a type picker
// over the types PMS SERVED for this slot, a drop target, and the files already
// on file with Remove. Shared by the Signal Advance evidence cards and the
// capacity request (BRK-S329), which file the same way.
//
// ── NOTHING HERE IS OPTIMISTIC (NN #7) ──────────────────────────────────────
//
// A file joins the list only from the upload's 201 (the row the server stored)
// and leaves it only on the DELETE's 2xx. A refusal names the file and the rule
// in the same words the client-side screen uses (`documentRuleMessage`).

export interface AccountDocumentSlotProps {
  accountId: string;
  /** Accessible name for the drop target, e.g. "Upload bank documents". */
  inputLabel: string;
  /** The accepted types PMS served — the picker offers exactly these. */
  types: readonly RequirementDocumentType[];
  rules: DocumentRules;
  /** The documents on file for this slot (the server's rows). */
  documents: readonly AccountDocument[];
  onUploaded: (document: AccountDocument) => void;
  onRemoved: (documentId: string) => void;
  /** False once the thing they support is with the underwriter. */
  canChange?: boolean;
}

export function AccountDocumentSlot({
  accountId,
  inputLabel,
  types,
  rules,
  documents,
  onUploaded,
  onRemoved,
  canChange = true,
}: AccountDocumentSlotProps) {
  const { authedRequest, authedUpload } = useAuth();
  const listId = useId();
  // A single accepted type needs no picker; otherwise the broker chooses.
  const [type, setType] = useState<string>(
    types.length === 1 ? types[0].key : "",
  );
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const labelFor = (key: string) =>
    types.find((t) => t.key === key)?.label ?? null;

  async function upload(file: File) {
    setError(null);
    if (type === "") {
      setError("Choose what kind of document this is first.");
      return;
    }
    const rejection = screenDocument(file, rules);
    if (rejection) {
      setError(rejection.message);
      return;
    }
    setBusy(true);
    try {
      onUploaded(
        await uploadAccountDocument(authedUpload, accountId, { file, type }),
      );
    } catch (err) {
      const refused = documentRejection(err);
      setError(
        refused
          ? documentRuleMessage(refused.rule, refused.filename, rules)
          : err instanceof ApiError
            ? messageFor(err)
            : "We couldn’t upload that file. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(documentId: string) {
    setError(null);
    setRemoving(documentId);
    try {
      await deleteAccountDocument(authedRequest, accountId, documentId);
      onRemoved(documentId);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p className="text-sm text-brk-dim">
        Accepted: {types.map((t) => t.label).join(", ")}
      </p>
      {canChange && (
        <>
          {types.length > 1 && (
            <div className="w-full md:w-72">
              <SelectField
                label="Document type"
                placeholder="Select…"
                options={types.map((t) => ({ value: t.key, label: t.label }))}
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={busy}
              />
            </div>
          )}
          <FileDropTarget
            prompt="Drag & drop a document, or"
            hint="One file at a time."
            inputLabel={inputLabel}
            accept={acceptAttribute(rules)}
            multiple={false}
            disabled={busy}
            onFiles={(files) => {
              const [file] = files;
              if (file) void upload(file);
            }}
          />
          {busy && (
            <p role="status" className="text-sm text-brk-dim">
              Uploading…
            </p>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {documents.length > 0 && (
        <ul
          aria-label={`${inputLabel}: files on file`}
          id={listId}
          className="flex flex-col gap-2"
        >
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border border-brk-border p-3 text-sm"
            >
              <span className="min-w-0 break-all text-brk-text">
                {doc.filename}
                {labelFor(doc.type) && (
                  <span className="text-brk-dim"> · {labelFor(doc.type)}</span>
                )}
              </span>
              {canChange && (
                <button
                  type="button"
                  onClick={() => void remove(doc.id)}
                  disabled={removing !== null}
                  aria-label={`Remove ${doc.filename}`}
                  className="text-sm font-medium text-brk-accent hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {removing === doc.id ? "Removing…" : "Remove"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
